import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import "./ui.scss";

const MENU_WIDTH = 196;
const ITEM_HEIGHT = 36;

/**
 * Ellipsis menu for row actions.
 *   items: [{ key, label, icon?, tone?: "danger", onClick }]
 * Rendered through a portal with fixed coordinates so the table's scroll
 * container can't clip it.
 */
const ActionMenu = ({ items, label = "Row actions" }) => {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  const close = useCallback(() => setPos(null), []);

  const open = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const estimatedHeight = items.length * ITEM_HEIGHT + 14;
    const left = Math.min(
      Math.max(8, rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - 8,
    );
    const fitsBelow = window.innerHeight - rect.bottom >= estimatedHeight + 8;
    const top = fitsBelow
      ? rect.bottom + 4
      : Math.max(8, rect.top - estimatedHeight - 4);
    setPos({ top, left });
  };

  useEffect(() => {
    if (!pos) return undefined;

    const onMouseDown = (e) => {
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      const inButton =
        buttonRef.current && buttonRef.current.contains(e.target);
      if (!inMenu && !inButton) close();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        close();
        if (buttonRef.current) buttonRef.current.focus();
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    // Move focus to the first item for keyboard users.
    const first = menuRef.current && menuRef.current.querySelector("button");
    if (first) first.focus();

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [pos, close]);

  const handleMenuKeyDown = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const buttons = Array.from(menuRef.current.querySelectorAll("button"));
    const index = buttons.indexOf(document.activeElement);
    const next =
      e.key === "ArrowDown"
        ? (index + 1) % buttons.length
        : (index - 1 + buttons.length) % buttons.length;
    buttons[next].focus();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="hr-iconbtn"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={Boolean(pos)}
        onClick={() => (pos ? close() : open())}
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>

      {pos &&
        createPortal(
          <div
            ref={menuRef}
            className="hr-menu"
            role="menu"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
            onKeyDown={handleMenuKeyDown}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={`hr-menu__item ${
                  item.tone === "danger" ? "hr-menu__item--danger" : ""
                }`}
                onClick={() => {
                  close();
                  item.onClick();
                }}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default ActionMenu;
